<template>
	<ValidationObserver v-slot="{ handleSubmit }" slim>
		<form class="personal_data_step" novalidate @submit.prevent="handleSubmit(emitContinue)">
			<h1 class="step_title">Datos personales</h1>
			<p class="step_subtitle">Empieza creando tu perfil.</p>
			<div class="field_row">
				<ValidationProvider
					v-slot="{ errors }"
					name="nombre"
					rules="required|min:2|max:49"
					slim
				>
					<div class="field">
						<label class="field_label" for="register_first_name">Nombre*</label>
						<input
							id="register_first_name"
							v-model.trim="form.first_name"
							class="field_input"
							:class="{ 'has-error': errors[0] }"
							type="text"
							placeholder="Ej. Juan Muñoz"
							autocomplete="given-name"
						/>
						<p v-if="errors[0]" class="field_error">{{ errors[0] }}</p>
					</div>
				</ValidationProvider>
				<ValidationProvider
					v-slot="{ errors }"
					name="apellido"
					rules="required|min:2|max:99"
					slim
				>
					<div class="field">
						<label class="field_label" for="register_last_name">Apellido*</label>
						<input
							id="register_last_name"
							v-model.trim="form.last_name"
							class="field_input"
							:class="{ 'has-error': errors[0] }"
							type="text"
							placeholder="Ej. Juan Muñoz"
							autocomplete="family-name"
						/>
						<p v-if="errors[0]" class="field_error">{{ errors[0] }}</p>
					</div>
				</ValidationProvider>
			</div>
			<ValidationProvider
				v-slot="{ errors }"
				name="correo electrónico"
				rules="required|email|max:250"
				:custom-messages="emailMessages"
				mode="eager"
				slim
			>
				<div class="field">
					<label class="field_label" for="register_email">Correo electrónico*</label>
					<div class="field_input_wrapper">
						<b-icon
							class="field_icon"
							pack="fas"
							icon="envelope"
							size="is-small"
						/>
						<input
							id="register_email"
							v-model.trim="form.email"
							class="field_input has-icon"
							:class="{
								'has-error': errors[0],
								'is-taken': emailExists && !errors[0],
							}"
							type="email"
							placeholder="nombre@empresa.com"
							autocomplete="username"
						/>
						<b-icon
							v-if="errors[0]"
							class="field_icon_error"
							pack="fas"
							icon="exclamation-circle"
							size="is-small"
						/>
					</div>
					<p v-if="errors[0]" class="field_error">{{ errors[0] }}</p>
					<p v-else-if="emailExists" class="field_taken_hint">
						Ya tienes una cuenta.
						<router-link class="field_taken_hint-link" :to="{ name: 'Login' }">
							Inicia sesión.
						</router-link>
					</p>
				</div>
			</ValidationProvider>
			<ValidationProvider
				v-slot="{ errors }"
				name="contraseña"
				rules="required|min:8|max:64"
				vid="password"
				slim
			>
				<div class="field">
					<label class="field_label" for="register_password">Contraseña*</label>
					<div class="field_input_wrapper">
						<input
							id="register_password"
							v-model="form.password"
							class="field_input has-action"
							:class="{ 'has-error': errors[0] }"
							:type="showPassword ? 'text' : 'password'"
							placeholder="Mínimo 8 caracteres"
							autocomplete="new-password"
						/>
						<button
							type="button"
							class="field_icon_action"
							:aria-label="
								showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'
							"
							@click="showPassword = !showPassword"
						>
							<b-icon
								pack="fas"
								:icon="showPassword ? 'eye-slash' : 'eye'"
								size="is-small"
							/>
						</button>
					</div>
					<p v-if="errors[0]" class="field_error">{{ errors[0] }}</p>
				</div>
			</ValidationProvider>
			<ValidationProvider
				v-slot="{ errors }"
				name="confirmación"
				rules="required|confirmed:password"
				slim
			>
				<div class="field">
					<label class="field_label" for="register_password_confirm">
						Repetir contraseña*
					</label>
					<div class="field_input_wrapper">
						<input
							id="register_password_confirm"
							v-model="form.password_confirmation"
							class="field_input has-action"
							:class="{ 'has-error': errors[0] }"
							:type="showPasswordConfirm ? 'text' : 'password'"
							placeholder="Repite tu contraseña"
							autocomplete="new-password"
						/>
						<button
							type="button"
							class="field_icon_action"
							:aria-label="
								showPasswordConfirm
									? 'Ocultar contraseña'
									: 'Mostrar contraseña'
							"
							@click="showPasswordConfirm = !showPasswordConfirm"
						>
							<b-icon
								pack="fas"
								:icon="showPasswordConfirm ? 'eye-slash' : 'eye'"
								size="is-small"
							/>
						</button>
					</div>
					<p v-if="errors[0]" class="field_error">{{ errors[0] }}</p>
				</div>
			</ValidationProvider>
			<ValidationProvider v-slot="{ errors }" name="rol" rules="required" slim>
				<div class="field">
					<label class="field_label" for="register_role">Rol</label>
					<div class="field_select_wrapper">
						<select
							id="register_role"
							v-model="form.role"
							class="field_input field_select"
							:class="{ 'is-placeholder': !form.role, 'has-error': errors[0] }"
						>
							<option value="" disabled>Seleccionar</option>
							<option
								v-for="option in roleOptions"
								:key="option.value"
								:value="option.value"
							>
								{{ option.label }}
							</option>
						</select>
					</div>
					<p v-if="errors[0]" class="field_error">{{ errors[0] }}</p>
				</div>
			</ValidationProvider>
			<button class="continue_button" type="submit" :disabled="!canContinue">
				Continuar
			</button>
		</form>
	</ValidationObserver>
</template>

<script>
import { consoleHandlerError } from "@/utils/message";
import { ROLE_OPTIONS } from "@/utils/roles";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { mapActions } from "vuex";

// Re-export para los consumidores/tests que ya importaban ROLE_OPTIONS desde
// este componente. La fuente de verdad vive en utils/roles.js (value + emoji),
// compartida con la vista del rol en el menú de usuario.
export { ROLE_OPTIONS };

export const EMAIL_CHECK_DEBOUNCE_MS = 450;

// Forma mínima de email para gatillar el check remoto de cuenta existente —
// la validación de formato real la hace la regla `email` de vee-validate.
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default {
	name: "RegisterPersonalDataStep",
	components: {
		ValidationObserver,
		ValidationProvider,
	},
	props: {
		value: {
			type: Object,
			default: () => ({}),
		},
	},
	data() {
		return {
			form: {
				first_name: this.value.first_name || "",
				last_name: this.value.last_name || "",
				email: this.value.email || "",
				password: this.value.password || "",
				password_confirmation: this.value.password_confirmation || "",
				role: this.value.role || "",
			},
			roleOptions: ROLE_OPTIONS,
			showPassword: false,
			showPasswordConfirm: false,
			emailExists: false,
			emailCheckTimer: null,
			emailMessages: {
				email: "¡Ups! Parece que tu correo tiene un error.",
			},
		};
	},
	computed: {
		isComplete() {
			return Object.values(this.form).every((field) => field !== "");
		},
		canContinue() {
			return this.isComplete && !this.emailExists;
		},
	},
	watch: {
		"form.email"(newEmail) {
			this.emailExists = false;
			clearTimeout(this.emailCheckTimer);
			if (!EMAIL_SHAPE.test(newEmail)) return;
			this.emailCheckTimer = setTimeout(
				() => this.checkEmailExists(newEmail),
				EMAIL_CHECK_DEBOUNCE_MS
			);
		},
	},
	beforeDestroy() {
		clearTimeout(this.emailCheckTimer);
	},
	methods: {
		...mapActions("userAuth", ["checkActiveUser"]),
		checkEmailExists(email) {
			this.checkActiveUser(email)
				.then((data) => {
					// El usuario pudo seguir tipeando durante el request: solo
					// aplica si la respuesta corresponde al valor actual.
					if (this.form.email === email) {
						this.emailExists = data === true;
					}
				})
				.catch((error) => {
					// Check informativo: si falla no bloquea el registro.
					consoleHandlerError({ error, silent: true, prefix: "[register]" });
				});
		},
		emitContinue() {
			this.$emit("continue", { ...this.form });
		},
	},
};
</script>
